import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import Icon from '../../common/Icons';
import { AP_COPY } from '../adminPersonnelCopy';
import './NotificationPanel.css';

/**
 * NotificationPanel - Panneau de notifications
 * 
 * @param {array} notifications - Liste des notifications
 *   [{
 *     id,
 *     type: 'info' | 'success' | 'warning' | 'danger',
 *     title,
 *     message,
 *     timestamp,
 *     read,
 *     action: {label, onClick}
 *   }]
 * @param {boolean} loading - État de chargement
 * @param {function} onMarkAsRead - Callback pour marquer comme lu
 * @param {function} onViewAll - Callback pour voir toutes les notifications
 */
const NotificationPanel = ({ 
  notifications = [], 
  loading = false,
  onMarkAsRead,
  onViewAll
}) => {
  const unreadCount = notifications.filter(n => !n.read).length;

  const getNotificationIcon = (type) => {
    const icons = {
      info: 'infoCircle',
      success: 'checkCircle',
      warning: 'alertTriangle',
      danger: 'alertCircle',
      system: 'bell'
    };
    return icons[type] || 'bell';
  };

  const getNotificationColor = (type) => {
    const colors = {
      info: 'info',
      success: 'success',
      warning: 'warning',
      danger: 'danger',
      system: 'primary'
    };
    return colors[type] || 'secondary';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short' 
    });
  };

  if (loading) {
    return (
      <Card className="notification-panel border-0 shadow-sm h-100">
        <Card.Header className="notification-header">
          <h5 className="mb-0">{AP_COPY.NOTIFICATIONS_TITLE}</h5>
        </Card.Header>
        <Card.Body>
          <div className="notification-list">
            {[1, 2, 3].map(i => (
              <div key={i} className="notification-item loading">
                <div className="skeleton skeleton-circle"></div>
                <div className="notification-content">
                  <div className="skeleton skeleton-text skeleton-lg mb-2"></div>
                  <div className="skeleton skeleton-text skeleton-sm"></div>
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <Card className="notification-panel border-0 shadow-sm h-100">
        <Card.Header className="notification-header">
          <h5 className="mb-0">{AP_COPY.NOTIFICATIONS_TITLE}</h5>
        </Card.Header>
        <Card.Body>
          <div className="notification-empty-state">
            <div className="empty-icon">
              <Icon name="bell" size={48} />
            </div>
            <p className="empty-text">{AP_COPY.NOTIFY_EMPTY}</p>
            <small className="empty-subtext">{AP_COPY.NOTIFY_EMPTY_SUB}</small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="notification-panel border-0 shadow-sm h-100">
      <Card.Header className="notification-header">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0">{AP_COPY.NOTIFICATIONS_TITLE}</h5>
          {unreadCount > 0 && (
            <Badge bg="danger" pill className="unread-badge">
              {unreadCount}
            </Badge>
          )}
        </div>
        {onViewAll && (
          <button className="btn-view-all" onClick={onViewAll}>
            {AP_COPY.BTN_VIEW_ALL}
            <Icon name="arrowRight" size={14} className="ms-1" />
          </button>
        )}
      </Card.Header>
      
      <Card.Body className="notification-body">
        <div className="notification-list">
          {notifications.map((notification) => {
            const color = getNotificationColor(notification.type);
            const icon = getNotificationIcon(notification.type);
            
            return (
              <div 
                key={notification.id} 
                className={`notification-item ${!notification.read ? 'unread' : ''}`}
              >
                {/* Indicateur non-lu */}
                {!notification.read && (
                  <div className="unread-indicator"></div>
                )}
                
                {/* Icon */}
                <div className={`notification-icon-wrapper bg-${color} bg-opacity-10`}>
                  <div className={`notification-icon text-${color}`}>
                    <Icon name={icon} size={18} />
                  </div>
                </div>
                
                {/* Content */}
                <div className="notification-content">
                  <div className="notification-main">
                    <h6 className="notification-title">
                      {notification.title}
                    </h6>
                    <span className="notification-time">
                      {formatTimestamp(notification.timestamp)}
                    </span>
                  </div>
                  
                  {notification.message && (
                    <p className="notification-message">
                      {notification.message}
                    </p>
                  )}
                  
                  {/* Actions */}
                  <div className="notification-actions">
                    {notification.action && (
                      <button 
                        className={`btn-notification-action text-${color}`}
                        onClick={notification.action.onClick}
                      >
                        {notification.action.label}
                      </button>
                    )}
                    
                    {!notification.read && onMarkAsRead && (
                      <button 
                        className="btn-mark-read"
                        onClick={() => onMarkAsRead(notification.id)}
                        title="Marquer comme lu"
                      >
                        <Icon name="check" size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

export default NotificationPanel;
